/**
 * Grid configuration constants
 * Centralized configuration values for consistent behavior across the grid implementation
 */

/** Default grid sizing and dimensions */
export const GRID_DEFAULTS = {
  /** Default number of rows per page for pagination - larger value reduces scroll jitter */
  PAGINATION_SIZE: 100,
  /** Default row height in pixels */
  ROW_HEIGHT: 36,
  /** Debounce block loading during rapid scrolling (ms) */
  BLOCK_LOAD_DEBOUNCE_MS: 100,
  /** Rows rendered outside viewport - higher = earlier prefetch, default is 10 */
  ROW_BUFFER: 50
} as const

/** Tooltip configuration with time-based delays */
export const TOOLTIP_DEFAULTS = {
  /** Delay before showing tooltips (milliseconds) - AG Grid default is 2000ms */
  SHOW_DELAY: 500,
  /** Delay before hiding tooltips (milliseconds) */
  HIDE_DELAY: 10000,
  /** Whether tooltips can be interacted with (hovered/clicked) */
  INTERACTION_ENABLED: true
} as const

/** Event names for Phoenix LiveView integration */
export const PHOENIX_EVENTS = {
  /** Default event name for cell editing */
  DEFAULT_EDITED: 'on_edited',
  /** Event for progressive data loading */
  PROGRESSIVE_LOAD: 'on_progressive_load',
  /** Event for column updates */
  COLUMNS_UPDATE: 'on_columns_update',
  /** Event for grid build/initialization */
  BUILD: 'on_build'
} as const

/** CSS class lists for component styling */
export const WRAPPER_CLASS_LIST = [
  'overflow-hidden',
  'w-full',
  'whitespace-nowrap',
  'h-full',
  'inline-flex',
  'items-center'
] as const satisfies readonly string[]

// AG Grid positioning using literal types
type RowPosition = 'top' | 'bottom'

/** Row position constants for AG Grid */
export const ROW_POSITIONS = {
  /** Bottom pinned row position for summary data */
  BOTTOM: 'bottom' as const,
  /** Top pinned row position for header data */
  TOP: 'top' as const
} as const satisfies Record<string, RowPosition>

// Cell formatter types using discriminated union approach
type FormatterType =
  | 'stepper'
  | 'badge'
  | 'badges'
  | 'icon'
  | 'icon_text'
  | 'progress'
  | 'redirect'
  | 'button'
  | 'image'
  | 'markdown'
  | 'number'
  | 'text'
  | 'plaintext'
  | 'mixed'
  | 'longtext'

/** Cell formatter type constants */
export const FORMATTER_TYPES = {
  /** Stepper component formatter */
  STEPPER: 'stepper',
  /** Single badge formatter */
  BADGE: 'badge',
  /** Multiple badges formatter */
  BADGES: 'badges',
  /** Icon formatter */
  ICON: 'icon',
  /** Icon with a primary label and optional metadata */
  ICON_TEXT: 'icon_text',
  /** Progress bar formatter */
  PROGRESS: 'progress',
  /** Redirect link formatter */
  REDIRECT: 'redirect',
  /** Button formatter */
  BUTTON: 'button',
  /** Image formatter with thumbnail preview */
  IMAGE: 'image',
  /** Sanitized markdown HTML formatter */
  MARKDOWN: 'markdown',
  /** Number formatter with locale support */
  NUMBER: 'number',
  /** Plain text formatter */
  TEXT: 'text',
  /** Plain text formatter (alias) */
  PLAINTEXT: 'plaintext',
  /** Per-cell formatter selected from cell payload metadata */
  MIXED: 'mixed',
  /** Long text formatter */
  LONGTEXT: 'longtext'
} as const satisfies Record<string, FormatterType>

// Cell editor types with type safety
type EditorType =
  | 'input'
  | 'textarea'
  | 'number'
  | 'checkbox'
  | 'date'
  | 'time'
  | 'datetime'
  | 'select'
  | 'list'
  | 'combobox'
  | 'tel'
  | 'tags'
  | 'stepper'
  | 'dropdown'

/** Cell editor type constants */
export const EDITOR_TYPES = {
  /** Text input editor */
  INPUT: 'input',
  /** Textarea editor */
  TEXTAREA: 'textarea',
  /** Number input editor */
  NUMBER: 'number',
  /** Boolean checkbox editor */
  CHECKBOX: 'checkbox',
  /** Date picker editor */
  DATE: 'date',
  /** Time picker editor */
  TIME: 'time',
  /** Local date and time editor */
  DATETIME: 'datetime',
  /** Static select editor */
  SELECT: 'select',
  /** Select dropdown editor */
  LIST: 'list',
  /** Lazy-loaded select editor */
  COMBOBOX: 'combobox',
  /** International telephone editor */
  TEL: 'tel',
  /** Existing-taxon multi-select editor */
  TAGS: 'tags',
  /** Stepper value editor */
  STEPPER: 'stepper',
  /** Custom dropdown editor */
  DROPDOWN: 'dropdown'
} as const satisfies Record<string, EditorType>

// AG Grid editor names with branded string types
type AGEditorName = `ag${string}CellEditor`

/** AG Grid built-in editor names */
export const AG_EDITORS = {
  /** AG Grid text cell editor */
  TEXT: 'agTextCellEditor' as AGEditorName,
  /** AG Grid large text cell editor */
  LARGE_TEXT: 'agLargeTextCellEditor' as AGEditorName,
  /** AG Grid number cell editor */
  NUMBER: 'agNumberCellEditor' as AGEditorName,
  /** AG Grid checkbox cell editor */
  CHECKBOX: 'agCheckboxCellEditor' as AGEditorName,
  /** AG Grid select cell editor */
  SELECT: 'agSelectCellEditor' as AGEditorName,
  /** AG Grid rich select cell editor with search and keyboard navigation */
  RICH_SELECT: 'agRichSelectCellEditor' as AGEditorName
} as const

// Grid model types matching AG Grid's expectations
type GridModelType = 'clientSide' | 'serverSide'

/** Grid model types */
export const GRID_MODEL_TYPES = {
  /** Client-side row model */
  CLIENT_SIDE: 'clientSide',
  /** Server-side row model */
  SERVER_SIDE: 'serverSide'
} as const satisfies Record<string, GridModelType>

/**
 * Formatters whose markup is heavier (multi-element badges/buttons/steppers
 * with inline icons) and therefore benefit from AG Grid's `deferRender`:
 * the renderer paints once the row is actually within the viewport instead
 * of building the markup synchronously during scroll. All of these now emit
 * plain light-DOM `innerHTML` (no custom elements), so deferral only saves
 * string-building work, not custom-element upgrade cost.
 *
 * The REDIRECT formatter stays eager so authoritative server updates replace
 * optimistic labels immediately instead of briefly restoring its skeleton.
 */
export const DEFERRED_RENDER_FORMATTERS = [
  'badge',
  'badges',
  'button',
  'stepper',
  'icon',
  'image',
  'progress'
] as const satisfies readonly FormatterType[]

// Type exports for external use
export type {
  AGEditorName,
  EditorType,
  FormatterType,
  GridModelType,
  RowPosition
}
